"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "@/lib/motion";

/* A fake mouse cursor for self-running product mockups. A mockup registers its
   clickable elements by name via `setTarget`, mounts <FakeCursor> inside its
   `relative` frame (which takes `frameRef`), and mutates its own state from the
   onMove/onClick callbacks as the cursor works through the script: each step
   rests, glides to its target, and "clicks" it (press + ripple). Target
   positions are measured fresh right before every glide, so layout changes and
   resizes self-correct. The engine is off (cursor null) under reduced motion,
   so mockups hold their first state. Decorative only. */

export const FAKE_CURSOR_MOVE_MS = 850;
const FAKE_CURSOR_CLICK_MS = 260;

/** Ref-registering callback used to name the cursor's click targets. */
export type SetCursorTarget = (name: string) => (el: Element | null) => void;

export interface FakeCursorStep {
  id: string;
  /** Name of the registered element to glide to. */
  target: string;
  /** Horizontal fraction along the target (default centered). */
  at?: number;
  /** Vertical fraction along the target (default just past middle). */
  atY?: number;
  /** Rest time before gliding, ms. */
  hold: number;
  /** Keep the button held after this click (start of a drag). */
  holdPress?: boolean;
  /** "release" arrives without pressing — ends a drag or just hovers. */
  arrive?: "press" | "release";
}

interface UseFakeCursorOptions {
  /** The looping script. Must be a stable (module-level) array. */
  script: readonly FakeCursorStep[];
  /** Target name to park the cursor on before the first step (defaults to the
      first step's target). */
  parkAt?: string;
  /** Fired as a step's glide starts — e.g. move a slider along with the cursor. */
  onMove?: (step: FakeCursorStep) => void;
  /** Fired when a step's click lands. */
  onClick?: (step: FakeCursorStep) => void;
}

export function useFakeCursor({ script, parkAt, onMove, onClick }: UseFakeCursorOptions) {
  const reduced = useReducedMotion();
  const [step, setStep] = useState(0);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [clicking, setClicking] = useState(false);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const targetsRef = useRef(new Map<string, Element>());
  const handlersRef = useRef({ onMove, onClick });

  useEffect(() => {
    handlersRef.current = { onMove, onClick };
  }, [onMove, onClick]);

  const setTarget = useCallback<SetCursorTarget>(
    (name) => (el) => {
      if (el) {
        targetsRef.current.set(name, el);
      } else {
        targetsRef.current.delete(name);
      }
    },
    []
  );

  /* A target's click point relative to the mock frame. */
  const resolvePoint = useCallback((target: string, at = 0.5, atY = 0.55) => {
    const frame = frameRef.current;
    const el = targetsRef.current.get(target);
    if (!frame || !el) return null;
    const f = frame.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return { x: r.left - f.left + r.width * at, y: r.top - f.top + r.height * atY };
  }, []);

  // Park the cursor once mounted (no-op after the first set).
  useEffect(() => {
    if (reduced) return;
    const first = script[0];
    setCursor(
      (current) =>
        current ??
        (parkAt
          ? resolvePoint(parkAt)
          : first
            ? resolvePoint(first.target, first.at, first.atY)
            : null)
    );
  }, [reduced, parkAt, script, resolvePoint]);

  /* One script step per effect run: release the previous click, rest, fire the
     move callback, glide to the target, then click and fire the click callback.
     Advancing `step` re-runs the effect for the next leg. */
  useEffect(() => {
    if (reduced || script.length === 0) return;
    const current = script[step % script.length];
    const previous = script[(step + script.length - 1) % script.length];
    const timers: ReturnType<typeof setTimeout>[] = [];
    let raf = 0;

    if (!previous.holdPress) {
      timers.push(setTimeout(() => setClicking(false), FAKE_CURSOR_CLICK_MS));
    }
    timers.push(
      setTimeout(() => {
        handlersRef.current.onMove?.(current);
        raf = requestAnimationFrame(() => {
          const point = resolvePoint(current.target, current.at, current.atY);
          if (point) setCursor(point);
        });
      }, current.hold)
    );
    timers.push(
      setTimeout(() => {
        setClicking(current.arrive !== "release");
        handlersRef.current.onClick?.(current);
        setStep((v) => v + 1);
      }, current.hold + FAKE_CURSOR_MOVE_MS)
    );
    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
  }, [step, reduced, script, resolvePoint]);

  return { frameRef, setTarget, cursor: reduced ? null : cursor, clicking };
}

interface FakeCursorProps {
  cursor: { x: number; y: number } | null;
  clicking: boolean;
}

/** The pointer itself — mount inside the mockup's `relative` frame. The arrow
    tip sits at the animated x/y point; a ripple expands from it on each click. */
export function FakeCursor({ cursor, clicking }: FakeCursorProps) {
  if (!cursor) return null;
  return (
    <motion.div
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-0 z-20"
      initial={false}
      animate={{ x: cursor.x, y: cursor.y, scale: clicking ? 0.8 : 1 }}
      transition={{
        x: { duration: FAKE_CURSOR_MOVE_MS / 1000, ease: [0.25, 0.8, 0.3, 1] },
        y: { duration: FAKE_CURSOR_MOVE_MS / 1000, ease: [0.25, 0.8, 0.3, 1] },
        scale: { duration: 0.12 },
      }}
    >
      <AnimatePresence>
        {clicking && (
          <motion.span
            initial={{ opacity: 0.35, scale: 0.4 }}
            animate={{ opacity: 0, scale: 1.8 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="absolute -top-3 -left-3 h-6 w-6 rounded-full"
            style={{ backgroundColor: "color-mix(in srgb, var(--foreground) 35%, transparent)" }}
          />
        )}
      </AnimatePresence>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <path
          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.85a.5.5 0 0 0-.85.36Z"
          fill="var(--foreground)"
          stroke="var(--background)"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </motion.div>
  );
}
